import moment from 'moment';

import type {
  ErpAiCourseLessonComparisonQuery,
  ErpAiGroupLessonSummaryQuery,
  ErpAiLessonAnalysesQuery,
  ErpAiLessonAnalysisQuery,
  ErpAiTeacherPracticePortraitQuery,
} from 'gqlhooks';
import { PATHS } from 'router/config';

/**
 * ТЗ 0104, §3.6 / §4 «Агент 4» — чистая модель анализа занятий: словари
 * строк сервера (graphene-енумов нет — строки), тона, шкалы, числа, ссылки.
 *
 * Без React — тестируется напрямую. Импортируется прямым путём
 * (`core/hooks/AiLessons/aiLessonModel`), не через барабан `core/hooks`:
 * имена потоков ИИ-агента там столкнулись бы.
 *
 * Методология (источник истины — skill `analyze-lesson-transcript` v2):
 * 7 критериев по шкале 0–4 с шагом 0,5, итог 0–100 — коучинговый балл,
 * «сила доказательств понимания» 0–100 — НЕ доля понявших студентов, ровно
 * одна рекомендация R01–R05; портрет практик — распределение (медиана,
 * Q1–Q3, n, покрытие, тренд), а не средняя, и НЕ для кадровых решений
 * (§32.7).
 */

// ── Типы ответа ────────────────────────────────────────────────────────────

export type AiLessonAnalysis = NonNullable<
  ErpAiLessonAnalysisQuery['aiLessonAnalysis']
>;
export type AiLessonCriterion = AiLessonAnalysis['criteria'][number];
export type AiLessonRecommendation = NonNullable<
  AiLessonAnalysis['recommendation']
>;
export type AiLessonSpeaker = AiLessonAnalysis['speakers'][number];
export type AiLessonAnalysisPage = ErpAiLessonAnalysesQuery['aiLessonAnalyses'];
export type AiLessonAnalysisRow = AiLessonAnalysisPage['rows'][number];
export type AiTeacherPracticePortrait = NonNullable<
  ErpAiTeacherPracticePortraitQuery['aiTeacherPracticePortrait']
>;
export type AiPracticeCriterionStat = AiTeacherPracticePortrait['criteria'][number];
export type AiCourseLessonComparison = ErpAiCourseLessonComparisonQuery['aiCourseLessonComparison'];
export type AiCourseLessonRow = AiCourseLessonComparison['rows'][number];
/** ТЗ 0123 — сводка разборов группы (вкладка «Аналитика занятий»). */
export type AiGroupLessonSummary = NonNullable<
  ErpAiGroupLessonSummaryQuery['aiGroupLessonSummary']
>;

// ── Словари ────────────────────────────────────────────────────────────────

const isOneOf = <T extends string>(list: readonly T[]) => (
  value: unknown,
): value is T => (list as readonly unknown[]).includes(value);

/** Статус разбора (`AiLessonAnalysisType.status`). */
export const AI_LESSON_STATUSES = [
  'queued',
  'running',
  'ready',
  'human_review_required',
  'insufficient_data',
  'failed',
] as const;
export type AiLessonStatus = typeof AI_LESSON_STATUSES[number];
export const isAiLessonStatus = isOneOf(AI_LESSON_STATUSES);

/** Разбор «в движении» — по нему идёт опрос раз в 4 с. */
export const AI_LESSON_POLLED_STATUSES: readonly string[] = [
  'queued',
  'running',
];

export const aiLessonPolled = (status: string | null | undefined): boolean =>
  Boolean(status) && AI_LESSON_POLLED_STATUSES.includes(status as string);

/**
 * Завершённый разбор с результатом — его методист может подтвердить или
 * не подтвердить (`RESULT_STATUSES` бэкенда; упавший — нет).
 */
export const AI_LESSON_RESULT_STATUSES: readonly string[] = [
  'ready',
  'human_review_required',
  'insufficient_data',
];

export const aiLessonHasResult = (status: string | null | undefined): boolean =>
  Boolean(status) && AI_LESSON_RESULT_STATUSES.includes(status as string);

/** Итоговая оценка занятия (`lessonStatus`): пороги 80 / 70 / 55. */
export const AI_LESSON_VERDICTS = [
  'strong',
  'generally_effective',
  'needs_targeted_improvement',
  'needs_methodologist_support',
  'insufficient_data',
  'human_review_required',
] as const;
export type AiLessonVerdict = typeof AI_LESSON_VERDICTS[number];
export const isAiLessonVerdict = isOneOf(AI_LESSON_VERDICTS);

/** Полоса силы доказательств понимания (`understandingBand`). */
export const AI_LESSON_BANDS = [
  'weak',
  'limited',
  'moderate',
  'strong',
  'very_strong',
  'insufficient_data',
] as const;
export type AiLessonBand = typeof AI_LESSON_BANDS[number];
export const isAiLessonBand = isOneOf(AI_LESSON_BANDS);

/** Статус оценки критерия. */
export const AI_LESSON_CRITERION_STATUSES = [
  'scored',
  'insufficient_data',
  'not_applicable',
  'blocked_by_quality',
  'human_review_required',
] as const;
export const isAiLessonCriterionStatus = isOneOf(AI_LESSON_CRITERION_STATUSES);

/** Уверенность в оценке критерия. */
export const AI_LESSON_CONFIDENCES = [
  'high',
  'medium',
  'low',
  'unknown',
] as const;
export const isAiLessonConfidence = isOneOf(AI_LESSON_CONFIDENCES);

/** Статус проверки методистом. */
export const AI_LESSON_REVIEW_STATUSES = [
  'none',
  'open',
  'reviewed_confirmed',
  'reviewed_not_confirmed',
] as const;
export type AiLessonReviewStatus = typeof AI_LESSON_REVIEW_STATUSES[number];
export const isAiLessonReviewStatus = isOneOf(AI_LESSON_REVIEW_STATUSES);

/** Решение методиста (`reviewAiLessonAnalysis(decision)`). */
export const AI_LESSON_DECISIONS = ['confirmed', 'not_confirmed'] as const;
export type AiLessonDecision = typeof AI_LESSON_DECISIONS[number];

/** Роль спикера в легенде кодов. */
export const AI_LESSON_SPEAKER_ROLES = ['teacher', 'student'] as const;
export const isAiLessonSpeakerRole = isOneOf(AI_LESSON_SPEAKER_ROLES);

/**
 * Зрелость портрета практик (методология §32.2): 1 занятие — коучинг
 * одного занятия, 2–3 — предварительные наблюдения, 4–5 — гипотезы, 6+ —
 * предварительный портрет, 10+ с внешними исходами — устойчивый.
 */
export const AI_PRACTICE_MATURITIES = [
  'lesson_only',
  'preliminary',
  'hypotheses',
  'provisional',
  'stable',
] as const;
export type AiPracticeMaturity = typeof AI_PRACTICE_MATURITIES[number];
export const isAiPracticeMaturity = isOneOf(AI_PRACTICE_MATURITIES);

/** Тренд критерия в портрете; `null` — меньше 4 оценок. */
export const AI_PRACTICE_TRENDS = ['up', 'down', 'flat'] as const;
export type AiPracticeTrend = typeof AI_PRACTICE_TRENDS[number];
export const isAiPracticeTrend = isOneOf(AI_PRACTICE_TRENDS);

/**
 * Коды отказов (`extensions.code`) запроса и решения — у каждого своя
 * фраза `aiLesson.refusal.<код>`; незнакомый — общая (`generic`).
 */
export const AI_LESSON_REFUSALS = [
  'no_transcript',
  'ai_disabled',
  'already_running',
  'budget_exceeded',
  'not_found',
  'bad_input',
  'forbidden',
  // ТЗ 0116: пакетный разбор и итог преподавателя.
  'internal_error',
  'not_enough_data',
  'cancelled',
  'daily_limit',
] as const;
export const isAiLessonRefusal = isOneOf(AI_LESSON_REFUSALS);

/** Ключ фразы отказа: свой у известного кода, общий — у остального. */
export const aiLessonRefusalKey = (code: string): string =>
  isAiLessonRefusal(code)
    ? `aiLesson.refusal.${code}`
    : 'aiLesson.refusal.generic';

/**
 * Семь критериев методологии в порядке рубрики — колонки сравнения между
 * группами. Подписи приходят с сервера (по-польски) в разборе; это —
 * порядок и запасная подпись.
 */
export const AI_LESSON_CRITERIA = [
  'practicality',
  'clarity_scaffolding',
  'structure_expectations',
  'understanding_checks',
  'interaction',
  'qa_feedback',
  'closure_transfer',
] as const;

// ── Тона ───────────────────────────────────────────────────────────────────

export type AiLessonTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'neutral'
  | 'info'
  | 'gold';

/**
 * Тон метки статуса разбора: в движении — синий, готов — зелёный, ждёт
 * методиста — золотой, данных мало — серый, упал — красный.
 */
export const AI_LESSON_STATUS_TONES: Readonly<
  Record<AiLessonStatus, AiLessonTone>
> = {
  queued: 'info',
  running: 'info',
  ready: 'ok',
  human_review_required: 'gold',
  insufficient_data: 'neutral',
  failed: 'danger',
};

/** Тон итоговой оценки занятия. */
export const AI_LESSON_VERDICT_TONES: Readonly<
  Record<AiLessonVerdict, AiLessonTone>
> = {
  strong: 'ok',
  generally_effective: 'info',
  needs_targeted_improvement: 'warn',
  needs_methodologist_support: 'danger',
  insufficient_data: 'neutral',
  human_review_required: 'gold',
};

export const aiLessonVerdictTone = (
  verdict: string | null | undefined,
): AiLessonTone =>
  isAiLessonVerdict(verdict) ? AI_LESSON_VERDICT_TONES[verdict] : 'neutral';

/** Тон проверки методистом: открыта — золотой, подтверждено — зелёный. */
export const AI_LESSON_REVIEW_TONES: Readonly<
  Record<AiLessonReviewStatus, AiLessonTone>
> = {
  none: 'neutral',
  open: 'gold',
  reviewed_confirmed: 'ok',
  reviewed_not_confirmed: 'danger',
};

/**
 * Тон балла 0–100 без вердикта (строка сравнения): пороги методологии
 * (80 / 70 / 55), как у `lessonStatus`.
 */
export const aiOverallTone = (
  score: number | null | undefined,
): AiLessonTone => {
  if (score === null || score === undefined || !Number.isFinite(score)) {
    return 'neutral';
  }
  if (score >= 80) return 'ok';
  if (score >= 70) return 'info';
  if (score >= 55) return 'warn';
  return 'danger';
};

/**
 * Тон балла критерия 0–4: 3 и выше — практика есть (зелёный), 2–2,5 —
 * частично (жёлтый), ниже 2 — зона роста (красный).
 */
export const aiCriterionTone = (
  score: number | null | undefined,
): AiLessonTone => {
  if (score === null || score === undefined || !Number.isFinite(score)) {
    return 'neutral';
  }
  if (score >= 3) return 'ok';
  if (score >= 2) return 'warn';
  return 'danger';
};

// ── Шкалы и числа ──────────────────────────────────────────────────────────

/** Шкала критерия: 0–4 с шагом 0,5. */
export const AI_CRITERION_MAX = 4;
export const AI_CRITERION_STEP = 0.5;

/**
 * Сколько половинных делений шкалы 0–4 закрашено (0…8): 2,5 → 5. Балл вне
 * шкалы прижимается к краям, дробь — к ближайшему шагу 0,5.
 */
export const aiCriterionHalfSteps = (
  score: number | null | undefined,
): number => {
  if (score === null || score === undefined || !Number.isFinite(score)) {
    return 0;
  }
  const clamped = Math.max(0, Math.min(AI_CRITERION_MAX, score));
  return Math.round(clamped / AI_CRITERION_STEP);
};

/** Положение на шкале 0–4 в процентах ширины (для полосы Q1–Q3). */
export const aiCriterionPercent = (
  value: number | null | undefined,
): number => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0;
  }
  return (
    (Math.max(0, Math.min(AI_CRITERION_MAX, value)) / AI_CRITERION_MAX) * 100
  );
};

/**
 * Числа — польской записью при любом языке интерфейса, как у остальных
 * экранов ИИ-агента («2,5»). Форматтеры создаются один раз.
 */
const SCORE_FMT = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 1,
});
const INT_FMT = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 });

/** Балл критерия или медиана: «2,5»; нет — «—». */
export const formatAiCriterionScore = (
  value: number | null | undefined,
): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : SCORE_FMT.format(value);

/** Балл 0–100 целым: «72»; нет — «—». */
export const formatAiOverall = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : INT_FMT.format(value);

/** Вес критерия (0,2) процентами: «20%». */
export const formatAiWeight = (weight: number | null | undefined): string =>
  weight === null || weight === undefined || !Number.isFinite(weight)
    ? '—'
    : `${INT_FMT.format(weight * 100)}%`;

/** Покрытие 0–1 процентами: «83%». */
export const formatAiCoverage = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : `${INT_FMT.format(value * 100)}%`;

/** Дата и время занятия/разбора: «09.10.2026 14:05». */
export const formatAiLessonDateTime = (
  iso: string | null | undefined,
): string => {
  if (!iso) return '—';
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : '—';
};

/** Дата занятия: «09.10.2026». */
export const formatAiLessonDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
};

/**
 * Список текстов модели для вывода: без пустых и без повторов (текст —
 * ключ строки списка; два одинаковых пункта — один и тот же пункт).
 */
export const aiUniqueTexts = (
  items: ReadonlyArray<string | null | undefined> | null | undefined,
): string[] =>
  Array.from(
    new Set(
      (items ?? [])
        .map((item) => (item ?? '').trim())
        .filter((item) => item.length > 0),
    ),
  );

/** «Следующий уровень» — по требованию в строке; пустые строки — прочь. */
export const aiNextLevelItems = (text: string | null | undefined): string[] =>
  (text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

// ── Сравнение между группами ───────────────────────────────────────────────

/**
 * `criteria` строки сравнения — `JSONString` `{criterionId: score|null}`
 * (строка с JSON или уже объект). Битый или пустой — пустой словарь, а
 * не падение экрана.
 */
export const parseAiCourseCriteria = (
  raw: unknown,
): Record<string, number | null> => {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => {
      const num = typeof item === 'number' ? item : Number(item);
      return [
        key,
        item === null ||
        item === undefined ||
        item === '' ||
        !Number.isFinite(num)
          ? null
          : num,
      ];
    }),
  );
};

/**
 * Колонки критериев сравнения: семь критериев рубрики по порядку, затем
 * незнакомые коды, которые встретились в строках (рубрика сменила версию —
 * колонка не пропадает молча).
 */
export const aiComparisonCriteriaIds = (
  rows: ReadonlyArray<Record<string, number | null>>,
): string[] => {
  const known = AI_LESSON_CRITERIA as readonly string[];
  const extra = new Set<string>();
  rows.forEach((row) =>
    Object.keys(row).forEach((key) => {
      if (!known.includes(key)) extra.add(key);
    }),
  );
  return [...known, ...Array.from(extra).sort()];
};

// ── Ссылки ─────────────────────────────────────────────────────────────────

/** Карточка урока (`/zoom/lessons/:id`) — ключ `GroupLesson`. */
export const aiLessonCardHref = (lessonId: string): string =>
  PATHS.zoomLesson.replace(':id', lessonId);

/**
 * Занятие, разбор которого открыт в боковой панели (`AiLessonAnalysisDrawer`).
 * Панель знает только `lessonId`; остальное — подпись шапки и то, что
 * открывший уже держит в руках:
 *  - `courseId` — для «Porównaj z innymi grupami»; нет — кнопки сравнения нет;
 *  - `hasTranscript` — `false`: транскрипта нет (кнопка запуска выключена с
 *    объяснением); не передан — неизвестно, и `no_transcript` объяснит отказ
 *    сервера.
 */
export interface IAiLessonTarget {
  lessonId: string;
  groupName?: string | null;
  lessonNumber?: number | null;
  lessonStartsAt?: string | null;
  courseId?: string | null;
  hasTranscript?: boolean;
}

/** Карточка группы (`/education/groups/:id/details`). */
export const aiGroupCardHref = (groupId: string): string =>
  PATHS.groupDetail.replace(':id', groupId);

/** ТЗ 0123: карточка группы сразу на вкладке «Аналитика занятий». */
export const aiGroupLessonAnalyticsHref = (groupId: string): string =>
  `${aiGroupCardHref(groupId)}?${new URLSearchParams({
    tab: 'lessonAnalytics',
  }).toString()}`;

/**
 * Выбор разбора занятия по его `lessonId` — чип таблицы занятий группы.
 * Сервер отдаёт по последнему разбору на занятие; на случай повтора
 * побеждает первый (новые первыми).
 */
export const aiAnalysesByLesson = (
  rows: ReadonlyArray<AiLessonAnalysisRow>,
): Map<string, AiLessonAnalysisRow> => {
  const map = new Map<string, AiLessonAnalysisRow>();
  rows.forEach((row) => {
    const key = String(row.lessonId);
    if (!map.has(key)) map.set(key, row);
  });
  return map;
};

/**
 * Перепроверка 0123: сколько разборов дошло до конца (не в очереди и не
 * в работе). Рост — опрос довёл разбор до результата или ошибки, и сводка
 * группы, считающая по готовым разборам, устарела.
 */
export const aiLessonSettledCount = (
  rows: ReadonlyArray<{ status?: string | null }>,
): number => rows.filter((row) => !aiLessonPolled(row.status)).length;

// ── ТЗ 0123: список занятий на вкладке «Аналитика занятий» ─────────────────

/** Фильтр списка: все / разобраны / ждут проверки методистом / не разобраны. */
export const AI_LESSON_LIST_FILTERS = [
  'all',
  'analyzed',
  'pending',
  'notAnalyzed',
] as const;
export type AiLessonListFilter = typeof AI_LESSON_LIST_FILTERS[number];
export const isAiLessonListFilter = isOneOf(AI_LESSON_LIST_FILTERS);

/**
 * Попадает ли прошедшее занятие под фильтр. `analysis` — последний разбор
 * занятия (или `undefined`): «разобрано» — у разбора есть результат,
 * «ждёт проверки» — методист ещё не решил (`reviewStatus = open`),
 * «не разобрано» — результата нет (разбора нет, он в работе или упал).
 */
export const aiLessonMatchesFilter = (
  filter: AiLessonListFilter,
  analysis: Pick<AiLessonAnalysisRow, 'status' | 'reviewStatus'> | undefined,
): boolean => {
  const hasResult = Boolean(analysis) && aiLessonHasResult(analysis?.status);
  switch (filter) {
    case 'analyzed':
      return hasResult;
    case 'pending':
      return analysis?.reviewStatus === 'open';
    case 'notAnalyzed':
      return !hasResult;
    default:
      return true;
  }
};
